import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import CopyRowButton from "../../components/alerts/CopyRowButton";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert as MuiAlert,
  TextField,
  Menu,
  ListItemText,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Backdrop,
  Badge,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CopyableValue from '../../components/common/CopyableValue';
import CopyableCardNumber from '../../components/alerts/CopyableCardNumber';
import { filterPanelSx } from "../../components/common/filterStyles";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { AlertStatus, AlertCmStatus, AlertSource, AlertsResponse, EthocaAlert, FraudAlertResolveStatus, DisputeAlertResolveStatus, AlertResolveRefunded, AlertType, SingleAlertResult, MatchVerificationStatus, AlertSubscriptionStatus, AlertSubscriptionStatusLabel, SUBSCRIPTION_STATUS_FILTER_OPTIONS, SUBSCRIPTION_STATUS_FILTER_VALUES, CM_STATUS_FILTER_OPTIONS, parseCmStatusFilter, serializeCmStatusFilter, isResolved, FulfillmentStatusLabel } from "../../types/alerts";
import MultiSelectFilter from "../../components/common/MultiSelectFilter";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import MatchVerificationChip from "../../components/alerts/MatchVerificationChip";
import SubscriptionStatusChip from "../../components/alerts/SubscriptionStatusChip";
import SubscriptionStatusDialog from "../../components/alerts/SubscriptionStatusDialog";
import SheetsMidSelect from "../../components/alerts/SheetsMidSelect";
import SheetsMidDialog from "../../components/alerts/SheetsMidDialog";
import FulfillmentStatusSelect from "../../components/alerts/FulfillmentStatusSelect";
import FulfillmentStatusDialog from "../../components/alerts/FulfillmentStatusDialog";
import TagsStatusSelect from "../../components/alerts/TagsStatusSelect";
import MisroutedDescriptorDialog from "../../components/alerts/MisroutedDescriptorDialog";
import { RequestStatusValues } from "../../types/autoRefund";
import { processAutoResolveResult } from "./autoResolveUtils";
import { useCompanyId } from "../../hooks/useCompanyId";
import { HasPermission } from "../../functions/auth";
import { useStateValue } from "../../context/StateValueContext";
import { hasPermission } from "../../functions/permissionUtils";
import { Agent } from "../../types/user";
import { PERMISSIONS } from "../../constants/permissions";
import ActivityLog from "./ActivityLog";
import { EthocaDescriptor, EthocaMerchant } from "../../types/ethocaDimensions";
import * as Sentry from "@sentry/react";
import EthocaCsvDialog from "../../components/alerts/EthocaCsvDialog";
import MapMerchantToCompanyDialog, { ETHOCA_PROVIDER_CONFIG, useUnassignedMerchants } from "../../components/alerts/MapMerchantToCompanyDialog";
import { pascalCase } from "../../utils/textUtils";
import AgeChip from "../../components/alerts/AgeChip";
import AlertCmStatusChip from "../../components/alerts/AlertCmStatusChip";
import MappingWarningChip from "../../components/alerts/MappingWarningChip";
import PreMatchedChip from "../../components/alerts/PreMatchedChip";
import { formatAgeLabel } from "../../utils/formatAge";
import { formatCurrency, formatAmountWithoutCurrency } from "../../utils/formatCurrency";
import { companySearchText } from "../../utils/companyUtils";
import { refundUnsettledWarning } from "../../utils/refundUnsettledWarning";
import { formatMisroutedDescriptorSaveMessage } from "../../utils/formatMisroutedDescriptorSaveResult";


// Column configuration
const COLUMNS_STORAGE_KEY = 'ethoca_alerts_visible_columns_v8';

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
  locked?: boolean;
  sortField?: string;
}

const allColumns: ColumnConfig[] = [
  { id: 'alert_id', label: 'Alert ID', isDefault: false },
  { id: 'cm_status', label: 'Status', isDefault: true, locked: true },
  { id: 'subscription_status', label: 'Subscription Status', isDefault: true, locked: true },
  { id: 'status', label: 'Status (Provider)', isDefault: false },
  { id: 'company_name', label: 'Company', isDefault: false },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'merchant', label: 'Merchant', isDefault: false },
  { id: 'descriptor', label: 'Descriptor', isDefault: true },
  { id: 'type', label: 'Type', isDefault: true },
  { id: 'alert_timestamp', label: 'Alert Timestamp', isDefault: true, sortField: 'alert_timestamp' },
  { id: 'age', label: 'Age', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'card_brand', label: 'Card Brand', isDefault: true },
  { id: 'card_number', label: 'Card Number', isDefault: true },
  { id: 'auth_code', label: 'Auth Code', isDefault: true },
  { id: 'transaction_id', label: 'Transaction ID', isDefault: true },
  { id: 'external_id', label: 'External ID', isDefault: false },
  { id: 'issuer', label: 'Issuer', isDefault: false },
  { id: 'arn', label: 'ARN', isDefault: false },
  { id: 'mcc', label: 'MCC', isDefault: false },
  { id: 'gateway', label: 'Gateway', isDefault: false },
  { id: 'gateway_id', label: 'Gateway ID', isDefault: false },
  { id: 'transaction_type', label: 'Transaction Type', isDefault: false },
  { id: 'resolved_at', label: 'Resolved At', isDefault: false },
  { id: 'billed_at', label: 'Billed At', isDefault: false },
  { id: 'refunded', label: 'Refunded', isDefault: false },
  { id: 'outcome', label: 'Outcome', isDefault: false },
  { id: 'transaction_timestamp', label: 'Transaction Timestamp', isDefault: false },
  { id: 'cm_notes', label: 'Notes', isDefault: false },
  { id: 'matched_order_url', label: 'Order URL', isDefault: false },
  { id: 'matched_gateway_title', label: 'Matched Gateway', isDefault: false },
  { id: 'google_sheet_mid', label: 'Sheets MID', isDefault: false },
  { id: 'fulfillment_status', label: 'Fulfillment', isDefault: false },
  { id: 'match_verification', label: 'Match Verification', isDefault: false },
  { id: 'warnings', label: 'Warnings', isDefault: false },
  { id: 'assigned_user', label: 'Assigned To', isDefault: true },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};


const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

// Helper to safely access metadata fields
const getMetadataField = (alert: EthocaAlert, field: string): any => {
  return alert.metadata?.[field] ?? null;
};

interface RecordStatusColor {
  main: string;
  bg: string;
}

const statusColors: Record<string, RecordStatusColor> = {
  resolved: { main: '#58C492', bg: '#EAFBF3' },
  pending: { main: '#F4C67A', bg: '#FFF8E5' },
};



// const stalenessColors: Record<string, string> = {
//   warning: '#f59e0b',
//   critical: '#ef4444',
// };




const isAlertResolved = (alert: EthocaAlert): boolean => {
  return alert.cm_status === AlertCmStatus.RESOLVED || alert.cm_status === AlertCmStatus.AUTO_RESOLVED;
};


const canBeResolvedManually = (status: AlertCmStatus): boolean => {
  return status === AlertCmStatus.QUEUED || status === AlertCmStatus.ACTION_NEEDED;
};



const VISA_REFUND_DISABLED_WARNING = 'Refunds for Visa alerts received via Ethoca are disabled for this company. Do not refund — resolve this alert as Other / Not Refunded.';

// Queued-for-automation alerts can't be assigned to a user.
const isAlertAssignable = (alert: EthocaAlert): boolean => {
  return alert.cm_status !== AlertCmStatus.QUEUED;
};

function AlertRow({
  alert, onResolve, visibleColumns, onAutoResolveStart, onAutoResolveEnd,
  selectionEnabled, selected, onToggleSelect,
}: {
  alert: EthocaAlert;
  onResolve: (success: boolean, message?: string) => void;
  visibleColumns: string[];
  onAutoResolveStart: () => void;
  onAutoResolveEnd: () => void;
  selectionEnabled: boolean;
  selected: boolean;
  onToggleSelect: (alertId: number) => void;
}) {
  const { post, patch, loading, error } = useApi();
  const [open, setOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<string>('');
  const [refunded, setRefunded] = useState<string>('');
  const [ppOrderUrl, setPpOrderUrl] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [resolveSubscriptionStatus, setResolveSubscriptionStatus] = useState<string>(alert.subscription_status);
  const [matchVerification, setMatchVerification] = useState<string>('');
  const [matchVerificationNotes, setMatchVerificationNotes] = useState<string>('');
  const [googleSheetMid, setGoogleSheetMid] = useState<string>('');
  const [fulfillmentStatus, setFulfillmentStatus] = useState<string>('');
  const [tagsStatus, setTagsStatus] = useState<string>('');
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [editNotes, setEditNotes] = useState<string>('');
  const [sheetsMidDialogOpen, setSheetsMidDialogOpen] = useState(false);
  const [fulfillmentStatusDialogOpen, setFulfillmentStatusDialogOpen] = useState(false);
  const [misroutedDescriptorDialogOpen, setMisroutedDescriptorDialogOpen] = useState(false);
  const [subscriptionStatusDialogOpen, setSubscriptionStatusDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Alert needs match verification if it was pre-matched but not yet verified
  const needsMatchVerification = !!alert.matched_order_url && !alert.match_verification_status;

  const refundUnsettled = alert.refund_verification_failed === true;
  const visaRefundDisabled = (alert.card_brand ?? '').toLowerCase() === 'visa' && alert.ethoca_visa_refund_enabled === false;

  const alertCompanyId = alert.company_id;
  const sheetsMidMissing = alertCompanyId != null && !googleSheetMid;
  const fulfillmentStatusMissing = !fulfillmentStatus;
  const tagsStatusMissing = !tagsStatus;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenResolveDialog = () => {
    handleCloseMenu();
    setResolveDialogOpen(true);
    setResolveStatus('');
    setRefunded('');
    setPpOrderUrl(alert.matched_order_url || '');
    setNotes('');
    setResolveSubscriptionStatus(alert.subscription_status);
    setMatchVerification('');
    setMatchVerificationNotes('');
    setGoogleSheetMid(alert.google_sheet_mid ?? '');
    setFulfillmentStatus(alert.fulfillment_status ?? '');
    setTagsStatus('');
  };


  const handleCloseResolveDialog = () => {
    const shouldRefresh = !!error;
    setResolveDialogOpen(false);
    setResolveStatus('');
    setRefunded('');
    setPpOrderUrl('');
    setNotes('');
    setResolveSubscriptionStatus(alert.subscription_status);
    setMatchVerification('');
    setMatchVerificationNotes('');
    setGoogleSheetMid('');
    setFulfillmentStatus('');
    setTagsStatus('');
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleOpenSubscriptionStatusDialog = () => {
    handleCloseMenu();
    setSubscriptionStatusDialogOpen(true);
  };

  const handleCloseSubscriptionStatusDialog = () => {
    const shouldRefresh = !!error;
    setSubscriptionStatusDialogOpen(false);
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  
  const handleSubscriptionStatusSaved = () => {
    setSubscriptionStatusDialogOpen(false);
    onResolve(true, 'Subscription status updated');
  };

  const handleOpenSheetsMidDialog = () => {
    handleCloseMenu();
    setSheetsMidDialogOpen(true);
  };

  const handleCloseSheetsMidDialog = () => {
    setSheetsMidDialogOpen(false);
  };

  const handleSheetsMidSaved = () => {
    setSheetsMidDialogOpen(false);
    onResolve(true, 'Sheets MID updated');
  };

  const handleOpenFulfillmentStatusDialog = () => {
    handleCloseMenu();
    setFulfillmentStatusDialogOpen(true);
  };

  const handleCloseFulfillmentStatusDialog = () => {
    setFulfillmentStatusDialogOpen(false);
  };

  const handleFulfillmentStatusSaved = () => {
    setFulfillmentStatusDialogOpen(false);
    onResolve(true, 'Fulfillment status updated');
  };

  const handleOpenMisroutedDescriptorDialog = () => {
    handleCloseMenu();
    setMisroutedDescriptorDialogOpen(true);
  };

  const handleCloseMisroutedDescriptorDialog = () => {
    setMisroutedDescriptorDialogOpen(false);
  };

  const handleMisroutedDescriptorSaved = (billedAlertsCount?: number) => {
    setMisroutedDescriptorDialogOpen(false);
    onResolve(true, formatMisroutedDescriptorSaveMessage(billedAlertsCount));
  };

  const handleOpenEditNotesDialog = () => {
    handleCloseMenu();
    setEditNotesDialogOpen(true);
    setEditNotes(alert.cm_notes || '');
  };

  const handleCloseEditNotesDialog = () => {
    const shouldRefresh = !!error;
    setEditNotesDialogOpen(false);
    setEditNotes('');
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleEditNotesDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') return;
    handleCloseEditNotesDialog();
  };

  const handleEditNotesSubmit = async () => {
    if (editNotes.trim() && !loading) {
      try {
        await patch(`/api/alerts/ethoca/notes`, {
          alertId: alert.alert_id,
          notes: editNotes.trim(),
        });
        handleCloseEditNotesDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to edit notes:', err);
      }
    }
  };

  const handleResolveDialogClose = (_event: unknown, reason: string) => {
    // Prevent closing when clicking outside (backdrop click)
    if (reason === 'backdropClick') {
      return;
    }
    handleCloseResolveDialog();
  };

  const handleFindPayment = () => {
    handleCloseMenu();
    const params = new URLSearchParams();
    if (alert.transaction_timestamp) {
      const txnDate = dayjs.utc(alert.transaction_timestamp);
      params.set('startDate', txnDate.subtract(1, 'day').format('YYYY-MM-DD'));
      params.set('endDate', txnDate.add(1, 'day').format('YYYY-MM-DD'));
    }
    if (alert.card_bin) params.set('cardBin', alert.card_bin);
    if (alert.card_last4) params.set('cardLast4', alert.card_last4);
    if (alert.auth_code) params.set('gwAuthCode', alert.auth_code);
    if (alert.pp_type) params.set('ppType', alert.pp_type);
    if (alert.pp_account_id) params.set('ppAccountId', alert.pp_account_id.toString());
    if (alertCompanyId) params.set('companyId', alertCompanyId.toString());
    window.open(`/payment-search?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleAutoResolve = async () => {
    handleCloseMenu();
    onAutoResolveStart();
    try {
      const result = await post<SingleAlertResult>(`/api/admin/alerts/process-single`, {
        alertId: alert.alert_id,
        alertSource: AlertSource.ETHOCA,
      });
      const outcome = processAutoResolveResult(result);
      onResolve(outcome.success, outcome.message);
    } catch (err) {
      console.error('Failed to auto-resolve alert:', err);
      onResolve(false, err instanceof Error ? err.message : 'Auto-resolve failed');
    } finally {
      onAutoResolveEnd();
    }
  };

  const handleMoveToQueue = async () => {
    handleCloseMenu();
    try {
      await post(`/api/admin/alerts/move-to-queue`, {
        alertId: alert.alert_id,
        alertSource: AlertSource.ETHOCA,
      });
      onResolve(true, 'Alert moved to queue');
    } catch (err) {
      console.error('Failed to move alert to queue:', err);
      onResolve(false, err instanceof Error ? err.message : 'Move to queue failed');
    }
  };

  const handleSync = async () => {
    handleCloseMenu();
    try {
      await post(`/api/alerts/ethoca/sync`, {
        alertId: alert.alert_id,
      });
      onResolve(true, 'Alert synced successfully');
    } catch (err) {
      console.error('Failed to sync alert:', err);
      onResolve(false, err instanceof Error ? err.message : 'Sync failed');
    }
  };

  const handleResolveSubmit = async () => {
    if (refundUnsettled) {
      try {
        await post(`/api/alerts/ethoca/refund-verification/resolve`, {
          alertId: alert.alert_id,
          notes: notes || undefined,
          ppOrderUrl: ppOrderUrl || undefined,
          subscriptionStatus: resolveSubscriptionStatus || undefined,
          ...(alertCompanyId != null && { googleSheetMid }),
          fulfillmentStatus,
        });
        handleCloseResolveDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to resolve unsettled refund:', err);
      }
      return;
    }
    if (resolveStatus && refunded && !loading && (!needsMatchVerification || matchVerification) && !sheetsMidMissing && !fulfillmentStatusMissing && !tagsStatusMissing) {
      try {
        await post(`/api/alerts/ethoca/resolve`, {
          status: resolveStatus,
          refunded,
          alert: {
            ...alert,
            logs: null
          },
          ppOrderUrl: ppOrderUrl || undefined,
          notes: notes || undefined,
          subscriptionStatus: resolveSubscriptionStatus || undefined,
          ...(alertCompanyId != null && { googleSheetMid }),
          fulfillmentStatus,
          ...(needsMatchVerification && {
            matchVerification,
            matchVerificationNotes: matchVerificationNotes || undefined,
          }),
        });
        handleCloseResolveDialog();
        onResolve(true);
      } catch (err) {
        // Error is handled by useApi hook and displayed via error state
        console.error('Failed to resolve alert:', err);
      }
    }
  };

  const getResolveStatusOptions = () => {
    if (alert.type === AlertType.FRAUD) {
      return Object.values(FraudAlertResolveStatus).map(status => ({
        value: status,
        label: pascalCase(status),
      }));
    } else if (alert.type === AlertType.DISPUTE) {
      return Object.values(DisputeAlertResolveStatus).map(status => ({
        value: status,
        label: pascalCase(status),
      }));
    }
    return [];
  };

  const getRefundedOptions = () => {
    return Object.values(AlertResolveRefunded).map(status => ({
      value: status,
      label: pascalCase(status),
    }));
  };

  const hasLatestLogFailed = (): boolean => {
    const logs = alert.logs;
    if (!logs || logs.length === 0) {
      return false;
    }
    // Logs are already sorted with latest one on top
    const latestLog = logs[0];
    return (
      latestLog.refund_request === RequestStatusValues.FAILED ||
      latestLog.alert_request === RequestStatusValues.FAILED
    );
  };

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'alert_id':
        return (
          <TableCell key={columnId}>
            {alert.ethoca_alert_id ? (
              <CopyableValue
                text={alert.ethoca_alert_id}
                copyTitle="Copy alert ID"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'cm_status':
        return (
          <TableCell key={columnId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AlertCmStatusChip status={alert.cm_status} />
              {alert.matched_order_url && !isResolved(alert.cm_status) && (
                <PreMatchedChip />
              )}
              {hasLatestLogFailed() && (
                <ErrorIcon
                  sx={{
                    color: '#dc2626',
                    fontSize: '20px',
                  }}
                />
              )}
              {!alert.company_id && (
                <Tooltip title="Not linked to any company" arrow>
                  <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: '20px' }} />
                </Tooltip>
              )}
              {alert.amount_exceeds_limit && (
                <Tooltip
                  title={`Amount exceeds the max refund amount${
                    alert.refund_max_amount != null
                      ? ` (${formatCurrency(alert.refund_max_amount, 'USD', true, 0)})`
                      : ''
                  }`}
                  arrow
                >
                  <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: '20px' }} />
                </Tooltip>
              )}
              {visaRefundDisabled && (
                <Tooltip title={VISA_REFUND_DISABLED_WARNING} arrow>
                  <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: '20px' }} />
                </Tooltip>
              )}
            </Box>
          </TableCell>
        );
      case 'subscription_status':
        return (
          <TableCell key={columnId}>
            <SubscriptionStatusChip status={alert.subscription_status} />
          </TableCell>
        );
      case 'assigned_user':
        return (
          <TableCell key={columnId}>
            {alert.assigned_user_id ? (
              <Tooltip title={alert.assigned_email ?? ''} placement="top-end" arrow>
                <span>{alert.assigned_first_name ?? alert.assigned_email}</span>
              </Tooltip>
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={columnId}>
            <Chip
              label={pascalCase(alert.status)}
              sx={{
                backgroundColor: statusColors[alert.status.toLowerCase()]?.bg || '#f3f4f6',
                color: statusColors[alert.status.toLowerCase()]?.main || '#6b7280',
                fontWeight: 500,
                borderRadius: '10px',
              }}
            />
          </TableCell>
        );
      case 'descriptor':
        return <TableCell key={columnId}>{alert.descriptor_name || '--'}</TableCell>;
      case 'type':
        return <TableCell key={columnId}>{alert.type || '--'}</TableCell>;
      case 'alert_timestamp':
        return <TableCell key={columnId}>{formatDateTime(alert.alert_timestamp)}</TableCell>;
      case 'age':
        return (
          <TableCell key={columnId}>
            <AgeChip ageHours={alert.age_hours} expiredThreshold={24} dangerThreshold={15} warningThreshold={12} />
          </TableCell>
        );
      case 'amount': {
        const cents = alert.alert_amount;
        if (cents === null || cents === undefined) return <TableCell key={columnId}>--</TableCell>;
        return (
          <TableCell key={columnId}>
            <CopyableValue
              text={formatCurrency(cents, alert.alert_currency ?? 'USD')}
              copyValue={formatAmountWithoutCurrency(cents, alert.alert_currency ?? 'USD')}
              copyTitle="Copy amount"
              revealOnHover
            />
          </TableCell>
        );
      }
      case 'card_brand':
        return (
          <TableCell key={columnId}>
            {alert.card_brand ? (
              <CopyableValue
                text={alert.card_brand}
                copyTitle="Copy card brand"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'card_number':
        return (
          <TableCell key={columnId}>
            <CopyableCardNumber cardBin={alert.card_bin} cardLast4={alert.card_last4} revealOnHover />
          </TableCell>
        );
      case 'auth_code':
        return (
          <TableCell key={columnId}>
            {alert.auth_code ? (
              <CopyableValue
                text={alert.auth_code}
                copyTitle="Copy auth code"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'transaction_id':
        return <TableCell key={columnId}>{alert.transaction_id || '--'}</TableCell>;
      case 'company_name':
        return <TableCell key={columnId}>{alert.company_name || '--'}</TableCell>;
      case 'brand_name':
        return <TableCell key={columnId}>{alert.brand_name || '--'}</TableCell>;
      case 'merchant':
        return <TableCell key={columnId}>{alert.merchant_name || '--'}</TableCell>;
      case 'main_descriptor':
        return <TableCell key={columnId}>{alert.main_descriptor_name || '--'}</TableCell>;
      case 'external_id':
        return <TableCell key={columnId}>{getMetadataField(alert, 'external_id') || '--'}</TableCell>;
      case 'issuer':
        return <TableCell key={columnId}>{getMetadataField(alert, 'issuer') || '--'}</TableCell>;
      case 'arn':
        return <TableCell key={columnId}>{alert.arn || '--'}</TableCell>;
      case 'mcc':
        return <TableCell key={columnId}>{getMetadataField(alert, 'mcc') || '--'}</TableCell>;
      case 'gateway':
        return <TableCell key={columnId}>{getMetadataField(alert, 'gateway') || '--'}</TableCell>;
      case 'gateway_id':
        return <TableCell key={columnId}>{getMetadataField(alert, 'gateway_id') || '--'}</TableCell>;
      case 'transaction_type':
        return <TableCell key={columnId}>{alert.transaction_type ? pascalCase(alert.transaction_type) : '--'}</TableCell>;
      case 'resolved_at':
        return <TableCell key={columnId}>{alert.resolved_at ? formatDateTime(alert.resolved_at) : '--'}</TableCell>;
      case 'billed_at':
        return <TableCell key={columnId}>{alert.billed_at ? formatDateTime(alert.billed_at) : '--'}</TableCell>;
      case 'refunded':
        return <TableCell key={columnId}>{alert.refunded ? pascalCase(alert.refunded) : '--'}</TableCell>;
      case 'outcome':
        return <TableCell key={columnId}>{alert.outcome ? pascalCase(alert.outcome) : '--'}</TableCell>;
      case 'transaction_timestamp': {
        const transactionDate = alert.transaction_timestamp
          ? formatDateTime(alert.transaction_timestamp)
          : null;
        return (
          <TableCell key={columnId}>
            {transactionDate ? (
              <CopyableValue
                text={transactionDate}
                copyTitle="Copy transaction timestamp"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      }
      case 'cm_notes':
        return <TableCell key={columnId}>{alert.cm_notes || '--'}</TableCell>;
      case 'matched_order_url':
        return (
          <TableCell key={columnId}>
            {alert.matched_order_url ? (
              <a href={alert.matched_order_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#465fff' }}>
                Open <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </a>
            ) : '--'}
          </TableCell>
        );
      case 'matched_gateway_title':
        return <TableCell key={columnId}>{alert.matched_gateway_title || '--'}</TableCell>;
      case 'google_sheet_mid':
        return <TableCell key={columnId}>{alert.google_sheet_mid || '--'}</TableCell>;
      case 'fulfillment_status':
        return <TableCell key={columnId}>{alert.fulfillment_status ? FulfillmentStatusLabel[alert.fulfillment_status] : '--'}</TableCell>;
      case 'match_verification':
        return (
          <TableCell key={columnId}>
            <MatchVerificationChip status={alert.match_verification_status} />
          </TableCell>
        );
      case 'warnings': {
        const missingCompany = !alert.company_id;
        const missingPp = !alert.pp_account_id;
        if (!missingCompany && !missingPp) {
          return <TableCell key={columnId}></TableCell>;
        }
        return (
          <TableCell key={columnId}>
            <Typography variant="body2" sx={{ color: '#ed6c02', whiteSpace: 'nowrap' }}>
              {missingCompany ? 'No company' : 'No PP Account'}
            </Typography>
          </TableCell>
        );
      }
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow key={alert.alert_id} hover onClick={() => setOpen(!open)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}>
        <TableCell padding="checkbox" sx={{ width: 48, textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
          {selectionEnabled && (
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelect(alert.alert_id)}
              disabled={!isAlertAssignable(alert)}
              size="small"
            />
          )}
        </TableCell>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <TableCell>
          <IconButton
            aria-label="more actions"
            aria-controls={menuOpen ? `alert-menu-${alert.alert_id}` : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleOpenMenu}
            size="small"
            sx={{ padding: '4px' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id={`alert-menu-${alert.alert_id}`}
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleFindPayment}>
              <ListItemText>Find Payment</ListItemText>
              <OpenInNewIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </MenuItem>
            {canBeResolvedManually(alert.cm_status) && (
              <MenuItem onClick={handleOpenResolveDialog}>
                <ListItemText>Resolve</ListItemText>
              </MenuItem>
            )}
            {canBeResolvedManually(alert.cm_status) && (
              <HasPermission permission={PERMISSIONS.ADMIN_PANEL}>
                <MenuItem onClick={handleAutoResolve}>
                  <ListItemText>Auto Resolve</ListItemText>
                </MenuItem>
              </HasPermission>
            )}
            {alert.cm_status === AlertCmStatus.ACTION_NEEDED && (
              <HasPermission permission={PERMISSIONS.ADMIN_PANEL}>
                <MenuItem onClick={handleMoveToQueue}>
                  <ListItemText>Move to Automation Queue</ListItemText>
                </MenuItem>
              </HasPermission>
            )}
            <MenuItem onClick={handleSync} disabled={loading}>
              <ListItemText>Refresh</ListItemText>
            </MenuItem>
            {alert.cm_notes && (
              <MenuItem onClick={handleOpenEditNotesDialog}>
                <ListItemText>Edit Notes</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenSubscriptionStatusDialog}>
              <ListItemText>Edit Subscription Status</ListItemText>
            </MenuItem>
            {alertCompanyId != null && (
              <MenuItem onClick={handleOpenSheetsMidDialog}>
                <ListItemText>Edit Sheets MID</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenFulfillmentStatusDialog}>
              <ListItemText>Edit Fulfillment Status</ListItemText>
            </MenuItem>
            {alertCompanyId != null && !!alert.descriptor_name && (
              <MenuItem onClick={handleOpenMisroutedDescriptorDialog}>
                <ListItemText>Mark Descriptor as Misrouted</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </TableCell>
      </TableRow>
      {alertCompanyId != null && sheetsMidDialogOpen && (
        <SheetsMidDialog
          open={sheetsMidDialogOpen}
          sheetsMidApiUrl="/api/alerts/ethoca/google-sheet-mid"
          alertId={alert.alert_id}
          alertLabel={alert.ethoca_alert_id}
          companyId={alertCompanyId}
          currentMid={alert.google_sheet_mid}
          onClose={handleCloseSheetsMidDialog}
          onSave={handleSheetsMidSaved}
        />
      )}
      {fulfillmentStatusDialogOpen && (
        <FulfillmentStatusDialog
          open={fulfillmentStatusDialogOpen}
          fulfillmentStatusApiUrl="/api/alerts/ethoca/fulfillment-status"
          alertId={alert.alert_id}
          alertLabel={alert.ethoca_alert_id}
          currentStatus={alert.fulfillment_status}
          onClose={handleCloseFulfillmentStatusDialog}
          onSave={handleFulfillmentStatusSaved}
        />
      )}
      {alertCompanyId != null && misroutedDescriptorDialogOpen && (
        <MisroutedDescriptorDialog
          open={misroutedDescriptorDialogOpen}
          companyId={alertCompanyId}
          initialDescriptor={alert.descriptor_name ?? ''}
          onClose={handleCloseMisroutedDescriptorDialog}
          onSaved={handleMisroutedDescriptorSaved}
        />
      )}
      {/* Subscription Status Dialog */}
      {subscriptionStatusDialogOpen && (
        <SubscriptionStatusDialog
          open={subscriptionStatusDialogOpen}
          alert={alert}
          onClose={handleCloseSubscriptionStatusDialog}
          onSave={handleSubscriptionStatusSaved}
        />
      )}
      {/* Edit Notes Dialog */}
      <Dialog
        open={editNotesDialogOpen}
        onClose={handleEditNotesDialogClose}
        aria-labelledby="edit-notes-dialog-title"
      >
        <DialogTitle id="edit-notes-dialog-title">
          Edit Notes — Alert #{alert.ethoca_alert_id}
        </DialogTitle>
        <DialogContent>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          <TextField
            size="small"
            fullWidth
            label="Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditNotesDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditNotesSubmit}
            variant="contained"
            color="primary"
            disabled={!editNotes.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleResolveDialogClose}
        aria-labelledby="resolve-alert-dialog-title"
        aria-describedby="resolve-alert-dialog-description"
      >
        <DialogTitle id="resolve-alert-dialog-title">
          Resolve Alert #{alert.ethoca_alert_id}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resolve-alert-dialog-description" sx={{ mb: 3 }}>
            Please select the resolve status and refunded status for this alert.
          </DialogContentText>
          {refundUnsettled && (
            <MuiAlert severity="warning" sx={{ mb: 2 }}>
              {refundUnsettledWarning('Ethoca', alert.pp_type)}
            </MuiAlert>
          )}
          {visaRefundDisabled && (
            <MuiAlert severity="warning" sx={{ mb: 2 }}>
              {VISA_REFUND_DISABLED_WARNING}
            </MuiAlert>
          )}
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          {alert.amount_exceeds_limit && (
            <MuiAlert severity="warning" sx={{ mb: 2 }}>
              Amount exceeds this account's max refund amount
              {alert.refund_max_amount != null ? ` (${formatCurrency(alert.refund_max_amount, 'USD', true)})` : ''}.
              Do not refund — resolve as Other / Not refunded.
            </MuiAlert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* First row: Resolve Status and Refunded */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControl size="small" fullWidth required disabled={loading || refundUnsettled}>
                <InputLabel id="resolve-status-label">Resolve Status</InputLabel>
                <Select
                  labelId="resolve-status-label"
                  id="resolve-status"
                  value={resolveStatus}
                  label="Resolve Status"
                  onChange={(e) => setResolveStatus(e.target.value)}
                >
                  {getResolveStatusOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth required disabled={loading || refundUnsettled}>
                <InputLabel id="refunded-label">Refunded</InputLabel>
                <Select
                  labelId="refunded-label"
                  id="refunded"
                  value={refunded}
                  label="Refunded"
                  onChange={(e) => setRefunded(e.target.value)}
                >
                  {getRefundedOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Second row: PP Order URL */}
            <TextField
              size="small"
              fullWidth
              label="Order URL"
              value={ppOrderUrl}
              onChange={(e) => setPpOrderUrl(e.target.value)}
              placeholder="https://..."
              helperText="Shopify: https://<shop>.myshopify.com/admin/orders/<id> (admin.shopify.com links are converted). CheckoutChamp: https://crm.checkoutchamp.com/customer/cs/orders/?orderId=<id> (customer page links are rejected). This field is for tracking purposes only and does not mean the order will be refunded automatically."
              disabled={loading}
            />
            {/* Subscription Status (optional) */}
            <FormControl size="small" fullWidth disabled={loading}>
              <InputLabel id="resolve-subscription-status-label">Subscription Status</InputLabel>
              <Select
                labelId="resolve-subscription-status-label"
                id="resolve-subscription-status"
                value={resolveSubscriptionStatus}
                label="Subscription Status"
                onChange={(e) => setResolveSubscriptionStatus(e.target.value)}
              >
                <MenuItem value={AlertSubscriptionStatus.ACTION_NEEDED}>
                  {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.ACTION_NEEDED]}
                </MenuItem>
                <MenuItem value={AlertSubscriptionStatus.CANCELLED}>
                  {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.CANCELLED]}
                </MenuItem>
                <MenuItem value={AlertSubscriptionStatus.NOT_SUBSCRIPTION}>
                  {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.NOT_SUBSCRIPTION]}
                </MenuItem>
                <MenuItem value={AlertSubscriptionStatus.NOT_FOUND}>
                  {AlertSubscriptionStatusLabel[AlertSubscriptionStatus.NOT_FOUND]}
                </MenuItem>
              </Select>
            </FormControl>
            {alertCompanyId != null && (
              <SheetsMidSelect
                companyId={alertCompanyId}
                value={googleSheetMid}
                onChange={setGoogleSheetMid}
                disabled={loading}
              />
            )}
            <FulfillmentStatusSelect
              value={fulfillmentStatus}
              onChange={setFulfillmentStatus}
              disabled={loading}
            />
            <TagsStatusSelect
              value={tagsStatus}
              onChange={setTagsStatus}
              disabled={loading}
            />
            {/* Third row: Notes */}
            <TextField
              size="small"
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              disabled={loading}
            />
            {/* Match Verification (required for pre-matched alerts) */}
            {needsMatchVerification && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Match Verification
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  This alert was pre-matched to an order. Was the match correct?
                </Typography>
                <FormControl size="small" fullWidth required disabled={loading || refundUnsettled}>
                  <InputLabel id="match-verification-label">Was this match correct?</InputLabel>
                  <Select
                    labelId="match-verification-label"
                    id="match-verification"
                    value={matchVerification}
                    label="Was this match correct?"
                    onChange={(e) => setMatchVerification(e.target.value)}
                  >
                    <MenuItem value={MatchVerificationStatus.CORRECT}>Correct</MenuItem>
                    <MenuItem value={MatchVerificationStatus.INCORRECT}>Incorrect</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  label="Verification Notes"
                  value={matchVerificationNotes}
                  onChange={(e) => setMatchVerificationNotes(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={4}
                  disabled={loading || refundUnsettled}
                  placeholder="Optional notes about the verification..."
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseResolveDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveSubmit}
            variant="contained"
            color="primary"
            autoFocus
            disabled={loading || sheetsMidMissing || fulfillmentStatusMissing || tagsStatusMissing || (!refundUnsettled && (!resolveStatus || !refunded || (needsMatchVerification && !matchVerification)))}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Alert Details
                </Typography>
                <CopyRowButton row={alert} columnSet="ethoca" />
              </Box>
              {visaRefundDisabled && (
                <MuiAlert severity="warning" sx={{ mb: 2, maxWidth: 1200 }}>
                  {VISA_REFUND_DISABLED_WARNING}
                </MuiAlert>
              )}
              {alert.amount_exceeds_limit && (
                <MuiAlert severity="warning" sx={{ mb: 2, maxWidth: 1200 }}>
                  Amount exceeds this account's max refund amount
                  {alert.refund_max_amount != null ? ` (${formatCurrency(alert.refund_max_amount, 'USD', true)})` : ''}.
                  Do not refund — resolve as Other / Not refunded.
                </MuiAlert>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, maxWidth: 1200 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Alert ID
                  </Typography>
                  {alert.ethoca_alert_id ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={alert.ethoca_alert_id}
                        copyTitle="Copy alert ID"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      --
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <AlertCmStatusChip status={alert.cm_status} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Subscription Status
                  </Typography>
                  <SubscriptionStatusChip status={alert.subscription_status} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status (Provider)
                  </Typography>
                  <Chip
                    label={pascalCase(alert.status)}
                    sx={{
                      backgroundColor: statusColors[alert.status.toLowerCase()]?.bg || '#f3f4f6',
                      color: statusColors[alert.status.toLowerCase()]?.main || '#6b7280',
                      fontWeight: 500,
                      borderRadius: '10px',
                    }}
                  />
                </Box>
                {alert.cm_notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.cm_notes}
                    </Typography>
                  </Box>
                )}
                {alert.matched_order_url && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order URL
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={alert.matched_order_url}
                        href={alert.matched_order_url}
                        copyTitle="Copy order URL"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                {alert.matched_gateway_title && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Matched Gateway
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.matched_gateway_title}
                    </Typography>
                  </Box>
                )}
                {alert.google_sheet_mid && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Sheets MID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.google_sheet_mid}
                    </Typography>
                  </Box>
                )}
                {alert.fulfillment_status && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Fulfillment Status
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {FulfillmentStatusLabel[alert.fulfillment_status]}
                    </Typography>
                  </Box>
                )}
                {alert.match_verification_status && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Match Verification
                    </Typography>
                    <MatchVerificationChip status={alert.match_verification_status} />
                  </Box>
                )}
                {alert.match_verification_notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Verification Notes
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.match_verification_notes}
                    </Typography>
                  </Box>
                )}
                {alert.match_verified_by && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Verified By
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.match_verified_by}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status Changed At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateTime(alert.cm_status_changed_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  {alert.company_name ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.company_name}
                    </Typography>
                  ) : (
                    <MappingWarningChip label="Not linked to any company" />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    PP Account
                  </Typography>
                  {alert.pp_account_id ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.pp_type ? `${alert.pp_type} / ` : ''}{alert.pp_account_id}
                    </Typography>
                  ) : (
                    <MappingWarningChip label="No PP Account" />
                  )}
                </Box>
                {alert.merchant_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Merchant
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.merchant_name}
                    </Typography>
                  </Box>
                )}
                {alert.descriptor_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Descriptor
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.descriptor_name}
                    </Typography>
                  </Box>
                )}
                {alert.main_descriptor_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Main Descriptor Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.main_descriptor_name}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'external_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      External ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'external_id')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'issuer') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Issuer
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'issuer')}
                    </Typography>
                  </Box>
                )}
                {alert.arn && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ARN
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.arn}
                    </Typography>
                  </Box>
                )}
                {alert.auth_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Auth Code
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={alert.auth_code}
                        copyTitle="Copy auth code"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                )}
                {alert.transaction_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.transaction_id}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'mcc') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      MCC
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'mcc')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'gateway') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Gateway
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'gateway')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'gateway_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Gateway ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'gateway_id')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'gateway_refund_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Gateway Refund ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'gateway_refund_id')}
                    </Typography>
                  </Box>
                )}
                {alert.transaction_type && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Transaction Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(alert.transaction_type)}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'consumer_name') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Consumer Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'consumer_name')}
                    </Typography>
                  </Box>
                )}
                {alert.refunded && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Refunded
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(alert.refunded)}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'message') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Message
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'message')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'external_source') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      External Source
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'external_source')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'external_liability') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      External Liability
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'external_liability')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'external_merchant_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      External Merchant ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'external_merchant_id')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'credited_at') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credited At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(getMetadataField(alert, 'credited_at'))}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'credit_outcome') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credit Outcome
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'credit_outcome')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'credit_reason') && Array.isArray(getMetadataField(alert, 'credit_reason')) && getMetadataField(alert, 'credit_reason').length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credit Reason
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'credit_reason').join(', ')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'credit_request_at') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credit Request At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(getMetadataField(alert, 'credit_request_at'))}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'chargeback_at') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Chargeback At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(getMetadataField(alert, 'chargeback_at'))}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'status_code') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'status_code')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'resolved_by') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Resolved By
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'resolved_by')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'overlap_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Overlap ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'overlap_id')}
                    </Typography>
                  </Box>
                )}
                {getMetadataField(alert, 'duplicate_id') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Duplicate ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getMetadataField(alert, 'duplicate_id')}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Outcome
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {alert.outcome ? pascalCase(alert.outcome) : '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Timestamp
                  </Typography>
                  {alert.transaction_timestamp ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={formatDateTime(alert.transaction_timestamp)}
                        copyTitle="Copy transaction timestamp"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      --
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateTime(alert.alert_timestamp)}
                  </Typography>
                </Box>
                {alert.resolved_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Resolved At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(alert.resolved_at)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Age
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {isAlertResolved(alert) && !alert.resolved_at ? '--' : formatAgeLabel(alert.age_hours)}
                  </Typography>
                </Box>
              </Box>

              <ActivityLog logs={alert.logs} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function Ethoca() {
  const { get, post, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [{ user, agents, miniCompanies }, dispatch] = useStateValue();
  const hasAssignPermission = useMemo(() => hasPermission(user, PERMISSIONS.ALERTS_ASSIGN), [user]);
  const [ethocaDescriptors, setEthocaDescriptors] = useState<EthocaDescriptor[]>([]);
  const [ethocaMerchants, setEthocaMerchants] = useState<EthocaMerchant[]>([]);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Read initial values from URL or use defaults
  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const initialStatuses = (urlSearchParams.get("statuses") || '').split(',').filter(Boolean);
  const initialDescriptorIds = (urlSearchParams.get("descriptorIds") || '').split(',').filter(Boolean);
  const initialMerchantIds = (urlSearchParams.get("merchantIds") || '').split(',').filter(Boolean);
  const initialCompanyIds = (urlSearchParams.get("companyIds") || '').split(',').filter(Boolean);
  const initialCmStatuses = parseCmStatusFilter(urlSearchParams.get("tab"));
  const initialSubscriptionStatuses = (urlSearchParams.get("subStatus") || '')
    .split(',')
    .filter(v => SUBSCRIPTION_STATUS_FILTER_VALUES.includes(v));
  const initialSearch = urlSearchParams.get("search") || '';
  const initialNoCompany = urlSearchParams.get("noCompany") === 'true';
  const initialNoPpAccount = urlSearchParams.get("noPpAccount") === 'true';
  const initialUnbilled = urlSearchParams.get("unbilled") === 'true';
  const initialMatchVerificationStatuses = (urlSearchParams.get("matchVerificationStatuses") || '').split(',').filter(Boolean);
  const initialPage = urlSearchParams.get("page") ? Math.max(0, parseInt(urlSearchParams.get("page")!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get("rowsPerPage") ? parseInt(urlSearchParams.get("rowsPerPage")!, 10) : 25;
  const initialSortBy = urlSearchParams.get("sortBy") || 'alert_timestamp';
  const initialSortOrder = (urlSearchParams.get("sortOrder") === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';
  const initialAssignedUserFilter = (urlSearchParams.get("userIds") || '').split(',').filter(Boolean);

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [statuses, setStatuses] = useState<string[]>(initialStatuses);
  const [descriptorIds, setDescriptorIds] = useState<string[]>(initialDescriptorIds);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [companyIds, setCompanyIds] = useState<string[]>(initialCompanyIds);
  const [search, setSearch] = useState<string>(initialSearch);
  const [noCompany, setNoCompany] = useState(initialNoCompany);
  const [noPpAccount, setNoPpAccount] = useState(initialNoPpAccount);
  const [unbilled, setUnbilled] = useState(initialUnbilled);
  const [matchVerificationStatuses, setMatchVerificationStatuses] = useState<string[]>(initialMatchVerificationStatuses);
  const [cmStatuses, setCmStatuses] = useState<string[]>(initialCmStatuses);
  const [subscriptionStatuses, setSubscriptionStatuses] = useState<string[]>(initialSubscriptionStatuses);
  const [alertsData, setAlertsData] = useState<AlertsResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [pageError, setPageError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Assignment + multi-select
  const [assignedUserFilter, setAssignedUserFilter] = useState<string[]>(initialAssignedUserFilter);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<number>>(new Set());
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignAgent, setBulkAssignAgent] = useState<number | null>(null);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  const [autoResolving, setAutoResolving] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [mapMerchantDialogOpen, setMapMerchantDialogOpen] = useState(false);
  const { merchants: unmappedMerchants, refresh: refreshUnmappedMerchants } = useUnassignedMerchants(ETHOCA_PROVIDER_CONFIG);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  // Filter row collapse
  const hasActiveFilters = statuses.length > 0 || merchantIds.length > 0 || companyIds.length > 0 || descriptorIds.length > 0 || search !== '' || noCompany || noPpAccount || unbilled || matchVerificationStatuses.length > 0;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount = (statuses.length > 0 ? 1 : 0) + (merchantIds.length > 0 ? 1 : 0) + (companyIds.length > 0 ? 1 : 0) + (descriptorIds.length > 0 ? 1 : 0) + (search !== '' ? 1 : 0) + (noCompany ? 1 : 0) + (noPpAccount ? 1 : 0) + (unbilled ? 1 : 0) + (matchVerificationStatuses.length > 0 ? 1 : 0);

  const assignedUserFilterOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'assigned', label: 'Assigned' },
    ...agents.map((agent) => ({ value: String(agent.userId), label: `${agent.name} (${agent.email})` })),
  ];

  const statusOptions = [
    { value: AlertStatus.PENDING, label: 'Pending' },
    { value: AlertStatus.RESOLVED, label: 'Resolved' },
  ];
  const merchantOptions = ethocaMerchants.map((merchant) => ({ value: String(merchant.provider_merchant_key), label: merchant.name ?? `Merchant ${merchant.provider_merchant_key}` }));
  const companyOptions = miniCompanies.map((c) => ({ value: String(c.id), label: c.name, searchText: companySearchText(c) }));
  const descriptorOptions = ethocaDescriptors.map((descriptor) => ({ value: String(descriptor.provider_descriptor_key), label: descriptor.name ?? `Descriptor ${descriptor.provider_descriptor_key}` }));
  const matchVerificationOptions = [
    { value: 'unverified', label: 'Unverified' },
    { value: MatchVerificationStatus.CORRECT, label: 'Correct' },
    { value: MatchVerificationStatus.INCORRECT, label: 'Incorrect' },
  ];

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const handleShowAllColumns = () => {
    setVisibleColumns(validColumnIds);
    saveStoredColumns(validColumnIds);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const fetchAlertDescriptors = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ descriptors: EthocaDescriptor[] }>(`/api/alerts/ethoca/descriptors${params}`);
      setEthocaDescriptors(response.descriptors);
    } catch (err) {
      console.error('Failed to fetch alert descriptors:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchAlertMerchants = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ merchants: EthocaMerchant[] }>(`/api/alerts/ethoca/merchants${params}`);
      setEthocaMerchants(response.merchants);
    } catch (err) {
      console.error('Failed to fetch alert merchants:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchAlertDescriptors();
  }, [fetchAlertDescriptors]);

  useEffect(() => {
    fetchAlertMerchants();
  }, [fetchAlertMerchants]);

  useEffect(() => {
    if (!user || agents.length > 0) return;
    const fetchAgents = async () => {
      try {
        const response = await get<Agent[]>('/api/users/agents');
        dispatch({ type: 'SET_AGENTS', agents: response });
      } catch (err) {
        console.error('Failed to fetch agents:', err);
        Sentry.captureException(err);
      }
    };
    fetchAgents();
  }, [user, agents.length, get, dispatch]);

  const toggleSelectAlert = useCallback((alertId: number) => {
    setSelectedAlertIds(prev => {
      const next = new Set(prev);
      if (next.has(alertId)) {
        next.delete(alertId);
      } else {
        next.add(alertId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedAlertIds(new Set()), []);

  // Update URL whenever search parameters change
  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) {
      params.set("startDate", startDate.format("YYYY-MM-DD"));
    } else {
      params.delete("startDate");
    }

    if (endDate) {
      params.set("endDate", endDate.format("YYYY-MM-DD"));
    } else {
      params.delete("endDate");
    }

    if (statuses.length > 0) {
      params.set("statuses", statuses.join(','));
    } else {
      params.delete("statuses");
    }

    if (descriptorIds.length > 0) {
      params.set("descriptorIds", descriptorIds.join(','));
    } else {
      params.delete("descriptorIds");
    }

    if (merchantIds.length > 0) {
      params.set("merchantIds", merchantIds.join(','));
    } else {
      params.delete("merchantIds");
    }

    if (companyIds.length > 0) {
      params.set("companyIds", companyIds.join(','));
    } else {
      params.delete("companyIds");
    }

    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }

    if (noCompany) {
      params.set("noCompany", "true");
    } else {
      params.delete("noCompany");
    }

    if (noPpAccount) {
      params.set("noPpAccount", "true");
    } else {
      params.delete("noPpAccount");
    }

    if (unbilled) {
      params.set("unbilled", "true");
    } else {
      params.delete("unbilled");
    }

    if (matchVerificationStatuses.length > 0) {
      params.set("matchVerificationStatuses", matchVerificationStatuses.join(','));
    } else {
      params.delete("matchVerificationStatuses");
    }

    const cmStatusParam = serializeCmStatusFilter(cmStatuses);
    if (cmStatusParam !== null) {
      params.set("tab", cmStatusParam);
    } else {
      params.delete("tab");
    }

    if (subscriptionStatuses.length > 0) {
      params.set("subStatus", subscriptionStatuses.join(','));
    } else {
      params.delete("subStatus");
    }

    if (page > 0) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }

    if (rowsPerPage > 0) {
      params.set("rowsPerPage", rowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }

    if (sortBy && sortBy !== 'alert_timestamp') {
      params.set("sortBy", sortBy);
    } else {
      params.delete("sortBy");
    }

    if (sortOrder !== 'asc') {
      params.set("sortOrder", sortOrder);
    } else {
      params.delete("sortOrder");
    }

    if (assignedUserFilter.length > 0) {
      params.set("userIds", assignedUserFilter.join(','));
    } else {
      params.delete("userIds");
    }

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statuses, descriptorIds, merchantIds, companyIds, search, noCompany, noPpAccount, unbilled, matchVerificationStatuses, cmStatuses, subscriptionStatuses, page, rowsPerPage, sortBy, sortOrder, assignedUserFilter]);

  const fetchAlerts = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    setPageError(null);

    try {
      const cmStatusesParam = cmStatuses.length > 0 ? cmStatuses.join(',') : '';
      const subscriptionStatusesParam = subscriptionStatuses.join(',');
      const companyIdsParam = companyIds.length > 0 ? companyIds.join(',') : (companyId ? companyId.toString() : '');

      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        ...(statuses.length > 0 && { statuses: statuses.join(',') }),
        ...(descriptorIds.length > 0 && { descriptorIds: descriptorIds.join(',') }),
        ...(merchantIds.length > 0 && { merchantIds: merchantIds.join(',') }),
        ...(companyIdsParam && { companyIds: companyIdsParam }),
        ...(search && { search }),
        ...(noCompany && { noCompany: 'true' }),
        ...(noPpAccount && { noPpAccount: 'true' }),
        ...(unbilled && { unbilled: 'true' }),
        ...(matchVerificationStatuses.length > 0 && { matchVerificationStatuses: matchVerificationStatuses.join(',') }),
        ...(cmStatusesParam && { cmStatuses: cmStatusesParam }),
        ...(subscriptionStatusesParam && { subscriptionStatuses: subscriptionStatusesParam }),
        ...(assignedUserFilter.length > 0 && { userIds: assignedUserFilter.join(',') }),
        ...(brandId && { brandId: String(brandId) }),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      const response = await get<AlertsResponse>(`/api/alerts/ethoca?${params.toString()}`);
      setAlertsData(response);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setAlertsData(null);
    }
  }, [get, startDate, endDate, statuses, descriptorIds, merchantIds, companyIds, search, noCompany, noPpAccount, unbilled, matchVerificationStatuses, cmStatuses, subscriptionStatuses, companyId, brandId, assignedUserFilter, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    clearSelection();
  }, [startDate, endDate, statuses, descriptorIds, merchantIds, companyIds, search, noCompany, noPpAccount, unbilled, matchVerificationStatuses, cmStatuses, subscriptionStatuses, companyId, brandId, assignedUserFilter, page, rowsPerPage, clearSelection]);


  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPage(0);
  };

  const handleChangePage = async (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleResolve = useCallback(async (success: boolean = true, message?: string) => {
    // Refresh alerts after resolve attempt
    await fetchAlerts();
    if (success) {
      setSnackbar({
        open: true,
        message: message || 'Alert resolved successfully',
        severity: 'success',
      });
    } else if (message) {
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  }, [fetchAlerts]);

  const pageAlerts = useMemo(() => alertsData?.alerts ?? [], [alertsData]);
  const selectableAlerts = useMemo(() => pageAlerts.filter(isAlertAssignable), [pageAlerts]);
  const allOnPageSelected = selectableAlerts.length > 0 && selectableAlerts.every(a => selectedAlertIds.has(a.alert_id));
  const someOnPageSelected = selectableAlerts.some(a => selectedAlertIds.has(a.alert_id)) && !allOnPageSelected;

  const handleSelectAll = () => {
    setSelectedAlertIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        selectableAlerts.forEach(a => next.delete(a.alert_id));
      } else {
        selectableAlerts.forEach(a => next.add(a.alert_id));
      }
      return next;
    });
  };

  const alreadyAssignedCount = useMemo(
    () => pageAlerts.filter(a => selectedAlertIds.has(a.alert_id) && a.assigned_user_id).length,
    [pageAlerts, selectedAlertIds],
  );

  const handleOpenBulkAssign = () => {
    setBulkAssignAgent(null);
    setBulkAssignError(null);
    setBulkAssignDialogOpen(true);
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignAgent) return;
    setBulkAssignLoading(true);
    setBulkAssignError(null);
    try {
      const count = selectedAlertIds.size;
      await post('/api/alerts/ethoca/assign', {
        alertIds: [...selectedAlertIds],
        userId: bulkAssignAgent,
      });
      await fetchAlerts();
      clearSelection();
      setBulkAssignDialogOpen(false);
      const agent = agents.find(a => a.userId === bulkAssignAgent);
      setSnackbar({
        open: true,
        message: `${count} alert${count !== 1 ? 's' : ''} assigned to ${agent?.name ?? 'user'}`,
        severity: 'success',
      });
    } catch (err) {
      setBulkAssignError(err instanceof Error ? err.message : 'Failed to assign alerts');
      Sentry.captureException(err);
    } finally {
      setBulkAssignLoading(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Alerts - Ethoca"
        description="Chargemont | Alerts - Ethoca"
      />
      <PageContainer>
        <PageHeader
          title="Ethoca Alerts"
          action={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <HasPermission permission={PERMISSIONS.ALERT_MERCHANT_TO_COMPANY}>
                <Badge
                  badgeContent={unmappedMerchants.length}
                  color="error"
                  max={99}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setMapMerchantDialogOpen(true)}
                    sx={{
                      borderColor: '#465fff',
                      color: '#465fff',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#3641f5',
                        backgroundColor: 'rgba(70, 95, 255, 0.04)',
                      },
                    }}
                  >
                    Link Merchant to Company
                  </Button>
                </Badge>
              </HasPermission>
              <HasPermission permission={PERMISSIONS.ALERTS_CSV}>
                <Button
                  variant="contained"
                  onClick={() => setCsvDialogOpen(true)}
                  sx={{
                    backgroundColor: '#465fff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3641f5',
                    },
                  }}
                >
                  Export CSV
                </Button>
              </HasPermission>
            </Box>
          }
        />

        {/* Filters */}
        <Box
          className="mb-6"
          sx={filterPanelSx}
        >
          {/* First Row: Date Pickers */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    —
                  </Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Right side: CM Status + Subscription Status filters + Filter Toggle */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <MultiSelectFilter
                label="Status"
                options={CM_STATUS_FILTER_OPTIONS}
                value={cmStatuses}
                onChange={(values) => {
                  setCmStatuses(values);
                  setPage(0);
                }}
              />
              <MultiSelectFilter
                label="Subscription Status"
                options={SUBSCRIPTION_STATUS_FILTER_OPTIONS}
                value={subscriptionStatuses}
                onChange={(values) => {
                  setSubscriptionStatuses(values);
                  setPage(0);
                }}
              />
              <SearchableMultiSelectFilter
                label="Assigned To"
                options={assignedUserFilterOptions}
                value={assignedUserFilter}
                placeholder="All"
                searchPlaceholder="Search agents…"
                onChange={(values) => {
                  setAssignedUserFilter(values);
                  setPage(0);
                }}
              />
              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                aria-label="Toggle advanced filters"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }}
                  />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Second Row: Filter Row (collapsible) */}
          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{
                px: 2,
                pb: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <MultiSelectFilter
                label="Status"
                options={statusOptions}
                value={statuses}
                width={150}
                onChange={(values) => { setStatuses(values); setPage(0); }}
              />
              {companyOptions.length > 0 && (
                <SearchableMultiSelectFilter
                  label="Company"
                  options={companyOptions}
                  value={companyIds}
                  width={200}
                  searchPlaceholder="Search companies…"
                  showSelectAllClearAll
                  onChange={(values) => { setCompanyIds(values); setPage(0); }}
                />
              )}
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={200}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <SearchableMultiSelectFilter
                label="Descriptor"
                options={descriptorOptions}
                value={descriptorIds}
                width={200}
                searchPlaceholder="Search descriptors…"
                showSelectAllClearAll
                onChange={(values) => { setDescriptorIds(values); setPage(0); }}
              />
              <MultiSelectFilter
                label="Match Verification"
                options={matchVerificationOptions}
                value={matchVerificationStatuses}
                width={190}
                onChange={(values) => { setMatchVerificationStatuses(values); setPage(0); }}
              />
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
                placeholder="Alert ID, auth code, txn ID"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noCompany}
                    onChange={(e) => { setNoCompany(e.target.checked); setPage(0); }}
                  />
                }
                label="Without company"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noPpAccount}
                    onChange={(e) => { setNoPpAccount(e.target.checked); setPage(0); }}
                  />
                }
                label="Without PP account"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={unbilled}
                    onChange={(e) => { setUnbilled(e.target.checked); setPage(0); }}
                  />
                }
                label="Unbilled only"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Bulk selection bar */}
        {hasAssignPermission && selectedAlertIds.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(70, 95, 255, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedAlertIds.size} selected
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleOpenBulkAssign}
              sx={{ backgroundColor: '#465fff', textTransform: 'none', '&:hover': { backgroundColor: '#3641f5' } }}
            >
              Assign selected
            </Button>
            <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {hasAssignPermission && (
                      <Checkbox
                        indeterminate={someOnPageSelected}
                        checked={allOnPageSelected}
                        onChange={handleSelectAll}
                        size="small"
                      />
                    )}
                  </TableCell>
                  {allColumns.filter(c => visibleColumns.includes(c.id)).map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortField ? (
                        <TableSortLabel
                          active={sortBy === column.sortField}
                          direction={sortBy === column.sortField ? sortOrder : 'asc'}
                          onClick={() => handleSort(column.sortField!)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : alertsData?.alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No alerts found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  alertsData?.alerts.map((alert) => (
                    <AlertRow key={alert.alert_id} alert={alert} onResolve={handleResolve} visibleColumns={visibleColumns} onAutoResolveStart={() => setAutoResolving(true)} onAutoResolveEnd={() => setAutoResolving(false)} selectionEnabled={hasAssignPermission} selected={selectedAlertIds.has(alert.alert_id)} onToggleSelect={toggleSelectAlert} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {alertsData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={alertsData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Columns
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              dense
              disabled={column.locked}
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                disabled={column.locked}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">
            Reset to Default
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleShowAllColumns} dense>
          <Typography variant="body2" color="primary">
            All Columns
          </Typography>
        </MenuItem>
      </Menu>

      {/* Auto Resolve Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={autoResolving}
      >
        <CircularProgress color="inherit" size={64} />
      </Backdrop>

      {/* Bulk Assign Dialog */}
      <Dialog
        open={bulkAssignDialogOpen}
        onClose={() => { if (!bulkAssignLoading) setBulkAssignDialogOpen(false); }}
        aria-labelledby="bulk-assign-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="bulk-assign-dialog-title">Assign Alerts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign {selectedAlertIds.size} alert{selectedAlertIds.size !== 1 ? 's' : ''} to:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-assign-agent-label">Select User</InputLabel>
            <Select
              labelId="bulk-assign-agent-label"
              value={bulkAssignAgent ?? ''}
              label="Select User"
              onChange={(e) => setBulkAssignAgent(e.target.value ? Number(e.target.value) : null)}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.userId} value={agent.userId}>
                  {agent.name} ({agent.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {alreadyAssignedCount > 0 && (
            <MuiAlert severity="warning" sx={{ mt: 2 }}>
              {alreadyAssignedCount} of {selectedAlertIds.size} alert{selectedAlertIds.size !== 1 ? 's' : ''} {alreadyAssignedCount === 1 ? 'is' : 'are'} already assigned and will be reassigned.
            </MuiAlert>
          )}
          {bulkAssignError && (
            <MuiAlert severity="error" sx={{ mt: 2 }}>
              {bulkAssignError}
            </MuiAlert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkAssignDialogOpen(false)} color="inherit" disabled={bulkAssignLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={!bulkAssignAgent || bulkAssignLoading}
            sx={{ backgroundColor: '#465fff', textTransform: 'none', '&:hover': { backgroundColor: '#3641f5' } }}
          >
            {bulkAssignLoading ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <EthocaCsvDialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} merchants={ethocaMerchants} companyId={companyId} />
      <MapMerchantToCompanyDialog
        open={mapMerchantDialogOpen}
        onClose={() => setMapMerchantDialogOpen(false)}
        onSuccess={() => {
          fetchAlerts();
          fetchAlertMerchants();
          refreshUnmappedMerchants();
        }}
        provider={ETHOCA_PROVIDER_CONFIG}
        merchants={unmappedMerchants}
      />
    </div>
  );
}
